/**
 * Consolidated Subscription Utilities
 * 
 * This file contains all subscription-related logic to avoid duplication
 * across components and ensure consistent parameter handling.
 */

/**
 * Validates if a plan price ID is valid
 * @param {string|null|undefined} planPriceId - The plan price ID to validate
 * @returns {boolean} - True if the plan price ID is valid
 */
export const isValidPlanPriceId = (planPriceId) => {
  return planPriceId && 
    planPriceId !== null && 
    planPriceId !== '' && 
    planPriceId !== 'null' && 
    planPriceId !== 'undefined';
};

/**
 * Determines if a subscription should be free tier based on plan price ID
 * @param {string|null|undefined} planPriceId - The plan price ID
 * @returns {boolean} - True if it should be a free tier subscription
 */
export const shouldBeFreeTier = (planPriceId) => {
  return !isValidPlanPriceId(planPriceId);
};

/**
 * Creates subscription data object with parameters matching backend StartSubscriptionCommand record
 * Backend expects: userId, planPriceId, paymentMethodId, freeTier, planCode, storageLimitSeconds
 * @param {Object} params - Subscription parameters
 * @param {number} params.userId - User ID
 * @param {string} params.planCode - Plan code
 * @param {string|null} params.planPriceId - Stripe price ID
 * @param {string|null} params.paymentMethodId - Stripe payment method ID
 * @param {number|null} params.storageLimitSeconds - Storage limit in seconds
 * @returns {Object} - Subscription data object with backend record field names
 */
export const createSubscriptionData = (params) => {
  const { userId, planCode, planPriceId, paymentMethodId, storageLimitSeconds } = params;
  
  // Send field names that match the backend StartSubscriptionCommand record fields
  const data = {
    userId: userId,          // ✅ Matches backend record field
    planPriceId: isValidPlanPriceId(planPriceId) ? planPriceId : null,  // ✅ Matches backend record field
    paymentMethodId: paymentMethodId,  // ✅ Matches backend record field
    freeTier: shouldBeFreeTier(planPriceId),  // ✅ Matches backend record field
    planCode: planCode,      // ✅ Matches backend record field
    storageLimitSeconds: storageLimitSeconds || null  // ✅ Matches backend record field
  };
  
  console.log('createSubscriptionData - created data:', data);
  console.log('createSubscriptionData - paymentMethodId:', data.paymentMethodId);
  console.log('createSubscriptionData - paymentMethodId type:', typeof data.paymentMethodId);
  
  return data;
};

/**
 * Extracts plan price ID from various possible field names
 * @param {Object} plan - Plan object
 * @returns {string|null} - The plan price ID or null if not found
 */
export const extractPlanPriceId = (plan) => {
  if (!plan) return null;
  
  return plan.stripePriceId || 
         plan.planPriceId || 
         plan.priceId || 
         plan.stripe_price_id || 
         plan.price_id || 
         plan.plan_price_id || 
         null;
};

/**
 * Validates subscription parameters before sending to backend
 * @param {Object} params - Subscription parameters
 * @returns {Object} - Validation result with success flag and errors array
 */
export const validateSubscriptionParams = (params) => {
  const { userId, planCode, planPriceId, paymentMethodId, freeTier } = params;
  const errors = [];
  
  if (!userId) {
    errors.push('User ID is required');
  }
  
  if (!planCode) {
    errors.push('Plan code is required');
  }
  
  // For paid subscriptions, validate payment method and price ID
  if (!freeTier) {
    if (!isValidPlanPriceId(planPriceId)) {
      errors.push('Plan price ID is required for paid subscriptions');
    }
    if (!paymentMethodId) {
      errors.push('Payment method ID is required for paid subscriptions');
    }
  }
  
  return {
    success: errors.length === 0,
    errors
  };
};

/**
 * Creates subscription polling function for post-payment verification
 * @param {Function} fetchFunction - Function to fetch subscription data
 * @param {number} maxAttempts - Maximum number of polling attempts
 * @param {number} intervalMs - Polling interval in milliseconds
 * @returns {Promise} - Promise that resolves with final subscription data
 */
export const createSubscriptionPoller = (fetchFunction, maxAttempts = 20, intervalMs = 2000) => {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    
    const poll = async () => {
      try {
        attempts++;
        const subscription = await fetchFunction();
        
        if (subscription && subscription.status === 'ACTIVE') {
          resolve(subscription);
          return;
        }
        
        if (attempts >= maxAttempts) {
          reject(new Error('Subscription polling timeout - please check your subscription status'));
          return;
        }
        
        setTimeout(poll, intervalMs);
      } catch (error) {
        if (attempts >= maxAttempts) {
          reject(error);
          return;
        }
        setTimeout(poll, intervalMs);
      }
    };
    
    poll();
  });
};